import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { Check, Star, Quote, Phone } from "lucide-react";
import {
  company,
  images,
  stats,
  homeServices,
  whyChoose,
  pricing,
  testimonials,
  trustBadges,
} from "../mock/mock";
import { SectionHeading, PrimaryButton, CTABanner, TaglineStrip } from "../components/Common";

const Home = () => {
  return (
    <div>
      {/* HERO */}
      <section className="relative brand-gradient overflow-hidden">
        <div className="absolute inset-0">
          <img src={images.ccFloor} alt="Contact center floor" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-dark via-navy-dark/90 to-navy/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="max-w-2xl fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-4 py-1.5 text-xs font-semibold text-gold-light mb-6">
              <Star size={13} className="fill-gold-light text-gold-light" /> UpCity Top Call Center — Durham, NC
            </div>
            <h1 className="font-heading font-extrabold text-white text-[38px] md:text-[56px] leading-[1.05]">
              Outsource your <span className="text-gold">Call Centre</span> Needs
            </h1>
            <p className="mt-6 text-white/80 text-lg leading-relaxed">
              Affordable, scalable and virtual staffing. 160 hours per agent every month with a
              30-day money-back guarantee. Lower your labor costs and grow your revenue with
              North Carolina's best contact centre partner.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <PrimaryButton
                href={`mailto:${company.salesEmail}?subject=Contact%20Center%20initial%20discussion`}
                gold
              >
                Book Free Consultation
              </PrimaryButton>
              <Link
                to="/what-we-do"
                className="inline-flex items-center gap-2 rounded-md border border-white/30 px-6 py-3.5 text-sm font-bold text-white hover:bg-white/10 transition-colors"
              >
                See All Services
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2">
              {trustBadges.map((b) => (
                <div key={b} className="flex items-center gap-2 text-white/70 text-xs font-medium">
                  <Check size={15} className="text-gold" /> {b}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <TaglineStrip />

      {/* STATS */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 -mt-14 relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {stats.map((s) => (
              <div key={s.label} className="bg-white rounded-xl shadow-lg border border-border p-6 text-center card-lift">
                <div className="font-heading font-extrabold text-navy text-3xl md:text-4xl">
                  {s.value}
                  <span className="text-gold">{s.suffix}</span>
                </div>
                <div className="mt-2 text-xs md:text-sm font-medium text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div className="img-zoom rounded-2xl shadow-xl relative">
            <img src={images.ccTwoWomen} alt="Customer service agents" className="w-full h-[420px] object-cover rounded-2xl" />
            <div className="absolute -bottom-6 -right-6 bg-gold rounded-xl p-5 shadow-lg hidden md:block">
              <div className="font-heading font-extrabold text-navy-dark text-3xl">30</div>
              <div className="text-navy-dark text-xs font-bold uppercase">Day Guarantee</div>
            </div>
          </div>
          <div>
            <SectionHeading
              eyebrow="Contact Centre Outsourcing Made Easy"
              title="Give us 30 days and we'll open your eyes to lower costs"
            />
            <p className="text-muted-foreground leading-relaxed mb-4 -mt-6">
              Many companies avoid outsource staffing fearing lack of control, communication issues,
              and problems with quality. That's until they discover Mid Atlantic Business Management.
              We've intentionally built our company to conquer these and other objections.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-6">
              We are your immediate solution for affordable, scalable and virtual staffing. See how we
              can help you in B2B lead generation, debt collection, answering services and insurance
              claims operations. Request your FREE customized Project Manifest today.
            </p>
            <PrimaryButton to="/what-we-do">Explore What We Do</PrimaryButton>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-secondary py-20">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            center
            eyebrow="What We Do"
            title="Full-service contact centre & business process outsourcing"
            subtitle="From HIPAA-compliant healthcare answering to B2B lead generation and debt recovery, we run the programs that keep your business moving."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {homeServices.map((s) => {
              const Icon = Icons[s.icon] || Icons.Circle;
              return (
                <Link
                  key={s.title}
                  to={s.to}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-border card-lift"
                >
                  <div className="img-zoom h-44 relative">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/70 to-transparent" />
                    <div className="absolute bottom-3 left-4 w-11 h-11 rounded-lg bg-gold flex items-center justify-center">
                      <Icon size={20} className="text-navy-dark" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading font-bold text-navy text-lg mb-2 group-hover:text-navy-light transition-colors">
                      {s.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4">{s.desc}</p>
                    <span className="inline-flex items-center gap-1 text-sm font-bold text-gold-dark">
                      Learn more <Icons.ArrowRight size={15} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            center
            eyebrow="Why Mid Atlantic"
            title="Everything you need in an outsourcing partner"
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((w) => {
              const Icon = Icons[w.icon] || Icons.Circle;
              return (
                <div key={w.title} className="rounded-2xl border border-border p-7 hover:border-gold transition-colors bg-white card-lift">
                  <div className="w-12 h-12 rounded-xl brand-gradient flex items-center justify-center mb-5">
                    <Icon size={22} className="text-gold-light" />
                  </div>
                  <h3 className="font-heading font-bold text-navy text-lg mb-2">{w.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{w.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="bg-secondary py-20">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            center
            eyebrow="Pricing Structure"
            title="Transparent outsourcing at highly competitive rates"
            subtitle="Choose the model that fits your team size. Save with our Annual Pre-Pay rate for teams of 10 or more agents."
          />
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {pricing.map((p) => (
              <div
                key={p.name}
                className={`rounded-2xl p-8 border card-lift ${
                  p.highlight
                    ? "brand-gradient text-white border-navy shadow-xl scale-[1.03]"
                    : "bg-white border-border"
                }`}
              >
                {p.highlight && (
                  <div className="inline-block rounded-full bg-gold px-3 py-1 text-[11px] font-bold text-navy-dark uppercase mb-4">
                    Most Popular
                  </div>
                )}
                <h3 className={`font-heading font-bold text-lg ${p.highlight ? "text-white" : "text-navy"}`}>
                  {p.name}
                </h3>
                <div className="mt-4 flex items-end gap-1">
                  <span className={`font-heading font-extrabold text-4xl ${p.highlight ? "text-gold-light" : "text-navy"}`}>
                    {p.price}
                  </span>
                  <span className={`text-sm mb-1 ${p.highlight ? "text-white/70" : "text-muted-foreground"}`}>
                    {p.period}
                  </span>
                </div>
                <div className={`text-xs mt-1 ${p.highlight ? "text-white/60" : "text-muted-foreground"}`}>{p.note}</div>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <Check size={17} className={p.highlight ? "text-gold-light shrink-0 mt-0.5" : "text-gold-dark shrink-0 mt-0.5"} />
                      <span className={p.highlight ? "text-white/85" : "text-muted-foreground"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className={`mt-8 block text-center rounded-md py-3 text-sm font-bold transition-colors ${
                    p.highlight ? "bg-gold text-navy-dark hover:bg-gold-light" : "bg-navy text-white hover:bg-navy-light"
                  }`}
                >
                  Get Started
                </Link>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-8">
            Full-time (160 hrs/month) &middot; Half resource (80 hrs/month) &middot; Annual pre-pay for 10+ agents &middot; Project pricing available
          </p>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-navy-dark py-20">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            center
            light
            eyebrow="Client Success"
            title="Trusted by healthcare, insurance and B2B leaders"
          />
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-white/5 border border-white/10 rounded-2xl p-7">
                <Quote size={30} className="text-gold mb-4" />
                <p className="text-white/80 leading-relaxed text-sm mb-6">“{t.quote}”</p>
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={15} className="fill-gold text-gold" />
                  ))}
                </div>
                <div className="font-heading font-bold text-white text-sm">{t.name}</div>
                <div className="text-white/50 text-xs">{t.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
};

export default Home;
